<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import {
		submitOfferFormClientSchema,
		hearAboutUsOptions,
		type HearAboutUsOptions
	} from "@fergmar/contracts/public/forms/offer-form.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { formatPhoneNumber } from "#lib/web-ui/helpers.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Textarea } from "#lib/web-ui/ui/textarea/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import * as Select from "#lib/web-ui/ui/select/index.js";
	import {
		PlacesAutoCompleteInput,
		VICTORIA_SERVICE_BOUNDS
	} from "#lib/web-ui/ui/places-input/index.js";
	import { PUBLIC_GOOGLE_PLACES_TOKEN } from "$app/env/public";
	import { toast } from "svelte-sonner";
	import { page } from "$app/state";
	import { dev } from "$app/env";
	import { TurnstileField } from "#lib/web-ui/forms/turnstile/index.js";
	import { getTemplateContext } from "../template-context.svelte";

	const templateContext = getTemplateContext();

	function pushToDataLayer(eventName: string) {
		if (typeof window !== "undefined" && window.dataLayer) {
			window.dataLayer.push({
				event: eventName,
				formId: "offer_form",
				pagePath: window.location.pathname
			});
		}
	}

	const form = superForm(
		defaults(
			{ address: page.url.searchParams.get("address") ?? "" },
			zod4(submitOfferFormClientSchema)
		),
		{
			id: "submit-offer",
			SPA: true,
			resetForm: true,
			validators: zod4(submitOfferFormClientSchema),
			async onUpdate({ form }) {
				if (!form.valid) return;

				try {
					await trpcBrowser.public.forms.submitOfferForm.mutate({
						...form.data,
						pageUrl: page.url.href,
						routeId: page.route.id ?? "",
						utmSource: page.url.searchParams.get("utm_source") ?? "",
						utmMedium: page.url.searchParams.get("utm_medium") ?? "",
						utmCampaign: page.url.searchParams.get("utm_campaign") ?? "",
						utmContent: page.url.searchParams.get("utm_content") ?? "",
						utmTerm: page.url.searchParams.get("utm_term") ?? ""
					});
				} catch (e) {
					form.data.turnstileToken = "";
					toast.error(
						e instanceof Error ? e.message : "Error submitting offer. Please try again."
					);
					pushToDataLayer("form_submission_error");
					return;
				}

				toast.success("Offer submitted successfully!");
				pushToDataLayer("form_submission_success");
				if (typeof window !== "undefined") window.fbq?.("track", "Lead");
			}
		}
	);

	const { form: formData, enhance, errors, submitting } = form;

	if (dev) {
		$formData.name = "John Doe";
		$formData.phone = "(123) 456-7890";
		$formData.email = "john.doe@example.com";
		$formData.hearAboutUs = "Web";
		$formData.homeDetails = "My home is a 3 bedroom, 2 bathroom house with a garage.";
	}
</script>

<form use:enhance method="POST" class="w-full space-y-4">
	<div class="space-y-2">
		<Label for="address">Home Address</Label>
		<PlacesAutoCompleteInput
			id="address"
			placesToken={PUBLIC_GOOGLE_PLACES_TOKEN}
			bounds={VICTORIA_SERVICE_BOUNDS}
			name="address"
			bind:value={$formData.address}
		/>
		{#if $errors.address}
			<p class="text-destructive text-sm">{$errors.address}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="name">Name</Label>
		<Input id="name" name="name" type="text" bind:value={$formData.name} autocomplete="name" />
		{#if $errors.name}
			<p class="text-destructive text-sm">{$errors.name}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="phone">Phone</Label>
		<Input
			id="phone"
			name="phone"
			type="tel"
			bind:value={$formData.phone}
			autocomplete="tel"
			onchange={(e) => {
				$formData.phone = formatPhoneNumber(e.currentTarget.value);
			}}
		/>
		{#if $errors.phone}
			<p class="text-destructive text-sm">{$errors.phone}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="email">Email</Label>
		<Input
			id="email"
			name="email"
			type="email"
			bind:value={$formData.email}
			autocomplete="email"
			onchange={(e) => {
				$formData.email = e.currentTarget.value.toLowerCase();
			}}
		/>
		{#if $errors.email}
			<p class="text-destructive text-sm">{$errors.email}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="hearAboutUs">How did you hear about us?</Label>
		<Select.Root
			type="single"
			name="hearAboutUs"
			value={$formData.hearAboutUs}
			onValueChange={(value) => {
				$formData.hearAboutUs = value as HearAboutUsOptions;
			}}
		>
			<Select.Trigger id="hearAboutUs" class="bg-background w-full">
				{$formData.hearAboutUs || "Select an option"}
			</Select.Trigger>
			<Select.Content>
				{#each hearAboutUsOptions.options as option (option)}
					<Select.Item value={option}>{option}</Select.Item>
				{/each}
			</Select.Content>
		</Select.Root>
		{#if $errors.hearAboutUs}
			<p class="text-destructive text-sm">{$errors.hearAboutUs}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="homeDetails">Tell us about your home</Label>
		<Textarea id="homeDetails" name="homeDetails" bind:value={$formData.homeDetails} />
		<p class="text-muted-foreground text-sm">
			Tell us about your home (Interior/exterior, recent upgrades, required
			maintenance/improvements)
		</p>
		{#if $errors.homeDetails}
			<p class="text-destructive text-sm">{$errors.homeDetails}</p>
		{/if}
	</div>

	<p class="text-muted-foreground text-sm">
		By submitting your phone number, you agree to receive text messages from {templateContext.siteName}
		regarding the sell of your home. You can opt-out anytime. Message and data rates may apply. View
		<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
		<a href="/privacy-policy" class="text-primary hover:underline">Privacy Policy</a>.
	</p>

	<div>
		<TurnstileField bind:token={$formData.turnstileToken} class="mb-4" />
	</div>

	<Button type="submit" disabled={$submitting} aria-busy={$submitting} class="w-full">
		{$submitting ? "Submitting..." : "Submit"}
	</Button>
</form>
